import { useState, useEffect } from 'react';
import { Label } from '@/components/atoms';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/atoms';
import { TwoFactorDialogWrapper } from './two-factor-dialog-wrapper';
import { useInitializeDisableTwoFactor, useDisableTwoFactor } from '@/api';

interface Disable2FAModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const Disable2FAModal = ({
  isOpen,
  onClose,
  onSuccess,
}: Disable2FAModalProps) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');

  const { mutate: initializeDisable, isPending: isInitializing } =
    useInitializeDisableTwoFactor();

  const { mutate: disable2FA, isPending: isDisabling } = useDisableTwoFactor(
    () => {
      onSuccess();
      onClose();
    }
  );

  useEffect(() => {
    if (isOpen) {
      initializeDisable();
      setOtp('');
      setError('');
    }
  }, [isOpen, initializeDisable]);

  const handleDisable = () => {
    if (otp.length !== 6) {
      setError('Invalid code. please try again');
      return;
    }
    disable2FA({ token: otp });
  };

  return (
    <TwoFactorDialogWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="DISABLING 2FA"
      onContinue={handleDisable}
      continueLabel="Disable 2FA"
      continueDisabled={otp.length !== 6 || isInitializing}
      isLoading={isDisabling}
    >
      <div className="flex flex-col gap-6 items-center py-4">
        <p className="text-sm text-center text-gray-600">
          To disable 2FA, please enter the 6-digit verification code sent to
          your email.
        </p>

        <div className="flex flex-col gap-3 w-full max-w-lg">
          <Label className="text-sm font-medium">
            Verification Code
            {error && (
              <span className="text-red-500 text-xs ml-auto float-right">
                {error}
              </span>
            )}
          </Label>
          <InputOTP
            maxLength={6}
            value={otp}
            onChange={(value) => {
              setOtp(value);
              setError('');
            }}
          >
            <InputOTPGroup className="gap-2 justify-center w-full">
              {Array.from({ length: 6 }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  className={error ? 'border-destructive bg-red-50' : ''}
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>
    </TwoFactorDialogWrapper>
  );
};
